import { useEffect } from 'react';

const DEFAULT_DESC = 'BIGTIME: relojería, belleza y accesorios al mayor desde 1 pieza. Precios mejores que Alibaba, entrega en 24-72h, garantía con video. Caracas · Maracaibo · Valencia.';
const SITE_URL = (typeof window !== 'undefined' ? window.location.origin : 'https://bigtimeca.com');

/**
 * Lightweight SEO hook — updates <title>, meta description/keywords, OpenGraph
 * tags, canonical URL, and injects JSON-LD schema when provided. Restores site
 * defaults on unmount. No external library.
 *
 * Fase E (iter 32): agrega canonical + schema markup nativo.
 */
export function useSEO({ title, description, keywords, canonical, schema, image } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, value, attr = 'name') => {
      if (!value) return null;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      const prev = el.getAttribute('content');
      el.setAttribute('content', value);
      return () => {
        if (created) { try { el.remove(); } catch {} }
        else if (prev != null) el.setAttribute('content', prev);
      };
    };

    // Canonical link — one per page, no duplicates.
    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      const created = !el;
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      return () => {
        if (created) { try { el.remove(); } catch {} }
        else if (prev != null) el.setAttribute('href', prev);
      };
    };

    // Schema.org JSON-LD injection. If a `schema` object is provided we insert
    // it as a <script type="application/ld+json"> and remove it on cleanup.
    const injectSchema = (obj) => {
      if (!obj) return null;
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo-schema', 'true');
      try { el.textContent = JSON.stringify(obj); } catch { return null; }
      document.head.appendChild(el);
      return () => { try { el.remove(); } catch {} };
    };

    const canonicalHref = canonical || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : SITE_URL);

    const restorers = [
      setMeta('description', description || DEFAULT_DESC),
      setMeta('keywords', keywords),
      setMeta('og:title', title, 'property'),
      setMeta('og:description', description || DEFAULT_DESC, 'property'),
      setMeta('og:url', canonicalHref, 'property'),
      setMeta('og:type', schema?.['@type'] === 'Product' ? 'product' : 'website', 'property'),
      image && setMeta('og:image', image, 'property'),
      setMeta('twitter:card', 'summary_large_image'),
      setMeta('twitter:title', title),
      setMeta('twitter:description', description || DEFAULT_DESC),
      image && setMeta('twitter:image', image),
      setCanonical(canonicalHref),
      injectSchema(schema),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r && r());
    };
  }, [title, description, keywords, canonical, image, JSON.stringify(schema || null)]);
}
